<template>
  <div
    :class="[
      'skill-dive-lab-row-brief',
      isLoading && 'skill-dive-lab-row-brief--disabled',
    ]"
  >
    <div class="skill-dive-lab-row-brief__content">
      <div class="skill-dive-lab-row-brief__content-title">
        <div>{{ title }}</div>
        <LockIcon
          v-if="locked"
          v-tooltip="{
            content: getAccessTooltip,
            placement: 'right-center',
          }"
          class="skill-dive-lab-row-brief__content-title--icon"
        />
        <RunningPill v-if="isLabRunning" :showLabel="false" />
      </div>
      <div class="skill-dive-lab-row-brief__content-bottom">
        <template v-if="isVulnerability">
          <div
            v-if="isVulnerability"
            class="skill-dive-lab-row-brief__content-bottom-vulnerability-label"
          >
            Vulnerabilities
          </div>
          <SeparatorBorder vertical height="13px" />
          <div
            class="skill-dive-lab-row-brief__content-bottom-vulnerability-cve"
          >
            {{ content.cve }}
          </div>
          <SeparatorBorder
            vertical
            height="13px"
            class="skill-dive-lab-row-brief__content-separator--hide-mobile"
          />
        </template>
        <template
          v-if="content.learning_areas && content.learning_areas.length"
        >
          <div
            v-for="learningArea in content.learning_areas"
            :key="learningArea.name"
            class="skill-dive-lab-row-brief__content-bottom-la"
          >
            <LearningAreaLabel
              :learningArea="learningArea.name"
              :showIcon="false"
              :dotIcon="!isSmallScreen"
              extraSmallMobile
            />
            <SeparatorBorder vertical height="13px" />
          </div>
        </template>
        <template v-if="difficulty">
          <div
            v-if="difficulty"
            class="skill-dive-lab-row-brief__content-bottom-difficulty"
          >
            {{ difficulty }}
          </div>
          <SeparatorBorder vertical height="13px" />
        </template>
        <template v-if="duration">
          <div class="skill-dive-lab-row-brief__content-bottom-duration">
            {{ duration }}
          </div>
          <SeparatorBorder
            v-if="isVulnerability && content.severity"
            vertical
            height="13px"
          />
        </template>
        <template v-if="isVulnerability">
          <div
            class="skill-dive-lab-row-brief__content-bottom-vulnerability-severity"
          >
            <span>{{ content.severity }}</span>
            <span><SeverityPill :severity="content.severity" small/></span>
          </div>
        </template>
        <SeparatorBorder
          v-if="completed"
          vertical
          height="13px"
          class="skill-dive-lab-row-brief__content-separator--hide-mobile"
        />
        <span
          v-if="completed"
          class="skill-dive-lab-row-brief__content-bottom-completed"
        >
          <GreencheckIcon /> Completed
        </span>
      </div>
    </div>
    <div class="skill-dive-lab-row-brief__info-btn-wrapper">
      <button
        v-tooltip="{
          content: 'More information',
          placement: 'top-center',
        }"
        @click.stop="toggleExpand"
      >
        <RotatingContainer :play="isLoading">
          <LoaderIcon v-if="isLoading" />
          <InfoIcon v-else />
        </RotatingContainer>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { RunningPill } from '@/components/properties'
import LearningAreaLabel from '@/components/properties/LearningAreaLabel.vue'
import { getLearningAreaName } from '@/helpers'
import RotatingContainer from '@/components/shared/RotatingContainer.vue'
import { InfoIcon, LoaderIcon, LockIcon } from 'vue-feather-icons'
import GreencheckIcon from '@/assets/icons/greencheck.svg'
import CONSTANTS from '@/constants'
import SeverityPill from '@/components/properties/SeverityPill.vue'
import SeparatorBorder from '@/components/shared/SeparatorBorder.vue'

export default {
  name: 'SkillDiveLabRowBrief',
  inject: ['getWindowWidth'],
  components: {
    RunningPill,
    LearningAreaLabel,
    RotatingContainer,
    LoaderIcon,
    InfoIcon,
    GreencheckIcon,
    LockIcon,
    SeverityPill,
    SeparatorBorder,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    isVulnerability: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState({
      runningLabSession: state => state.labs.runningLabSession,
      labUserStatuses: state => state.labs.labUserStatuses,
    }),
    ...mapGetters({
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isAccOwnerOrTeamManager: 'auth/isAccOwnerOrTeamManager',
      isBusinessUser: 'business/isBusinessUser',
    }),
    isLabRunning() {
      const { labId, runningLabSession } = this
      return labId === runningLabSession?.lab_id
    },
    labId() {
      return this.content.uuid || this.content.objectID
    },
    title() {
      return this.content.name
    },
    difficulty() {
      return this.content.difficulty ?? ''
    },
    duration() {
      return this.$duration.durationFromSecs(this.content.duration, 'minutes')
    },
    completed() {
      const { labId, labUserStatuses } = this
      const { FINISHED } = CONSTANTS.CONTENT_USER_STATUS
      return (
        this.content.user_status === FINISHED ||
        labUserStatuses[labId]?.user_lab_status === FINISHED
      )
    },
    getAccessTooltip() {
      if (this.locked) {
        if (this.isSkillDiveB2CUser) {
          return `This lab is part of the ${getLearningAreaName(
            this.content
          )} learning area. Get access.`
        } else if (this.isBusinessUser && !this.isAccOwnerOrTeamManager) {
          return 'This lab is locked. Request access.'
        } else {
          return 'This lab is locked. Get access.'
        }
      }
      return ''
    },
    isSmallScreen() {
      return this.getWindowWidth() < 600
    },
  },
  methods: {
    toggleExpand() {
      this.$emit('toggle-expand')
    },
  },
}
</script>
<style scoped lang="scss">
.skill-dive-lab-row-brief {
  min-height: 65px;
  display: grid;
  grid-template-columns: auto 40px;
  &__content {
    text-align: left;
    display: flex;
    flex-flow: column;
    gap: 5px;
    justify-content: space-evenly;
    @include mobile {
      gap: 10px;
    }
    &-title {
      display: flex;
      align-items: center;
      font-size: rem(12px);
      font-style: normal;
      font-weight: 700;
      line-height: rem(14px);
      width: 85%;
      @include mobile {
        font-size: rem(18px);
        font-weight: 500;
        line-height: rem(24px);
      }

      .running-pill {
        background: transparent;
      }

      &--icon {
        width: 40px;
        height: 40px;
        padding: 12px;
      }
    }

    &-bottom {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px);
      @include mobile {
        align-items: center;
      }
      &-completed {
        color: var(--positive-light);
        display: flex;
        align-items: center;
        gap: 5px;
        svg {
          width: 10px;
          padding: 0;
        }
      }
      &-difficulty {
        text-transform: capitalize;
      }
      &-vulnerability {
        &-label {
          color: var(--strong-light-green);
          font-size: rem(14px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(20px);
          display: flex;
          align-items: center;
        }
        &-cve {
          color: var(--light-blue-gray);
          font-size: rem(12px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(10px);
          flex-basis: 50%;
          @include mobile {
            flex-basis: unset;
          }
        }
        &-year {
          position: absolute;
          top: 0;
          right: 0;
        }
        &-severity {
          span {
            &:nth-of-type(1) {
              display: block;
              text-transform: capitalize;
              @include mobile {
                display: none;
              }
            }
            &:nth-of-type(2) {
              display: none;
              @include mobile {
                display: block;
              }
            }
          }
        }
      }
      &-la {
        display: flex;
        align-self: center;
        gap: 5px;
      }
    }
    &-separator {
      &--hide {
        display: none;
        &-mobile {
          display: none;
          @include mobile {
            display: block;
          }
        }
      }
    }
  }

  &__info-btn-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    @include mobile {
      grid-column: 2;
      grid-row: 1 / 4;
    }

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparent;
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      cursor: pointer;

      &:hover {
        background: var(--white-10);
      }

      svg {
        color: var(--white);
        width: 16px;
        height: 16px;
      }
    }
  }

  &--disabled {
    cursor: not-allowed;
  }
}
</style>
